import { JSX } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from "react-router-dom";
import LoginPage from "./pages/LoginPage.tsx";
import RegisterPage from "./pages/RegisterPage.tsx";
// import HomePage from "./pages/HomePage.tsx";
import { AuthProvider, useAuth } from "./contexts/AuthContext.tsx";
import ProductListPage from "./pages/ProductListPage.tsx";
import { CartProvider } from "./contexts/CartContext.tsx";
import CartPage from "./pages/CartPage.tsx";
import Navbar from "./components/Navbar.tsx";
import CheckoutPage from "./pages/CheckoutPage.tsx";
import ThankYouPage from "./pages/ThankYouPage.tsx";
import OrdersPage from "./pages/OrdersPage.tsx";
import "./output.css";
import DashboardPage from "./pages/admin/DashboardPage.tsx";
import AdminLayout from "./layouts/AdminLayout.tsx";
import CampaignManagementPage from "./pages/admin/CampaignManagementPage.tsx";
import OrderManagementPage from "./pages/admin/OrderManagementPage.tsx";
import ProductManagementPage from "./pages/admin/ProductManagementPage.tsx";
import UserManagementPage from "./pages/admin/UserManagementPage.tsx";
import { ErrorProvider } from "./contexts/ErrorContext.tsx";
import ErrorToast from "./components/ErrorToast.tsx";
import { SuccessProvider } from "./contexts/SuccessContext.tsx";
import SuccessToast from "./components/SuccessToast.tsx";
import ProfilePage from "./pages/ProfilePage.tsx";
import ProductDetailPage from "./pages/ProductDetailPage.tsx";
import ActivatePage from "./pages/ActivatePage.tsx";
import SearchPage from "./pages/SearchPage.tsx";
import CampaignProductsPage from "./pages/CampaignProductsPage.tsx";

const PrivateRoute = ({ children }: { children: JSX.Element }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }: { children: JSX.Element }) => {
  const { isAuthenticated, user } = useAuth();
  return isAuthenticated && user?.role === "Admin"
    ? children
    : <Navigate to="/login" />;
};

// Define your routes using createBrowserRouter
const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <>
        <Navbar />
        <ErrorToast />
        <SuccessToast />
        <Outlet />
      </>
    ),
    children: [
      { path: "", element: <ProductListPage /> },
      { path: "search", element: <SearchPage /> },
      { path: "products", element: <ProductListPage /> },
      { path: "campaigns", element: <CampaignProductsPage /> },
      { path: "product/:id", element: <ProductDetailPage /> },
      { path: "login", element: <LoginPage /> },
      { path: "register", element: <RegisterPage /> },
      { path: "cart", element: <CartPage /> },
      { path: "checkout", element: <CheckoutPage /> },
      { path: "thank-you", element: <ThankYouPage /> },
      { path: "activate", element: <ActivatePage /> },
      { path: "orders", element: <PrivateRoute><OrdersPage /></PrivateRoute> },
      { path: "profile", element: <PrivateRoute><ProfilePage /></PrivateRoute> },
      {
        path: "admin",
        element: <AdminRoute><AdminLayout /></AdminRoute>,
        children: [
          { path: "dashboard", element: <AdminRoute><DashboardPage /></AdminRoute> },
          { path: "users", element: <AdminRoute><UserManagementPage /></AdminRoute> },
          { path: "products", element: <AdminRoute><ProductManagementPage /></AdminRoute> },
          { path: "campaigns", element: <AdminRoute><CampaignManagementPage /></AdminRoute> },
          { path: "orders", element: <AdminRoute><OrderManagementPage /></AdminRoute> },
        ],
      },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);

const App = () => (
  <ErrorProvider>
    <SuccessProvider>
      <AuthProvider>
        <CartProvider>
          <RouterProvider router={router} />
        </CartProvider>
      </AuthProvider>
    </SuccessProvider>
  </ErrorProvider>
);

export default App;